import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getClient, from, rpc, SessionProvider, unwrap, type ModuleStatus, type OrgInfo, type Role, type SessionInfo } from "@vixio/data";
import { Button, Callout, VixioSymbol } from "@vixio/ui";
import { useAuth } from "../auth/AuthProvider";
import { AuthLayout } from "../auth/AuthLayout";
import { TotpChallengeForm } from "../auth/mfa";
import { isEnabled, registeredModules, type ModuleState } from "./registry";
import { CreateOrganizationForm } from "@vixio/core-ui";

const ORG_KEY = "vixio.org";

function readOrg(): string | null {
  try {
    return localStorage.getItem(ORG_KEY);
  } catch {
    return null;
  }
}
function writeOrg(id: string) {
  try {
    localStorage.setItem(ORG_KEY, id);
  } catch {
    /* navegação privada */
  }
}

interface Boot {
  orgs: OrgInfo[];
  isPlatformAdmin: boolean;
  installed: Set<string>;
}

const ModuleStateCtx = createContext<ModuleState>({ installed: new Set(), flags: new Map() });
export function useModuleState() {
  return useContext(ModuleStateCtx);
}

/** Depois do login: 2FA pendente, organizações, módulos instalados e flags da organização. */
export function SessionGate({ children }: { children: ReactNode }) {
  const { session, aal, refreshAal } = useAuth();
  const qc = useQueryClient();
  const userId = session!.user.id;

  const mfa = useQuery({
    queryKey: ["auth", "aal", userId, aal],
    queryFn: async () => {
      const { data } = await getClient().auth.mfa.getAuthenticatorAssuranceLevel();
      if (data && data.nextLevel === "aal2" && data.currentLevel !== "aal2") {
        const { data: f } = await getClient().auth.mfa.listFactors();
        return f?.totp?.find((x) => x.status === "verified")?.id ?? null;
      }
      return null;
    },
  });

  const boot = useQuery({
    queryKey: ["core", "boot", userId],
    queryFn: async (): Promise<Boot> => {
      const [orgs, admin, installed] = await Promise.all([
        unwrap<Array<OrgInfo & { role: Role }>>(from("core", "v_my_organizations").select("*").order("name")),
        rpc("core", "is_platform_admin", {}),
        rpc("core", "installed_modules", {}) as Promise<Array<{ module: string }> | null>,
      ]);
      return {
        orgs: orgs.map((o) => ({ ...o, brand: (o.brand ?? {}) as Record<string, unknown> })),
        isPlatformAdmin: admin === true,
        installed: new Set((installed ?? []).map((r) => r.module)),
      };
    },
  });

  const [orgId, setOrgId] = useState<string | null>(() => readOrg());
  const orgs = boot.data?.orgs ?? [];
  const org = orgs.find((o) => o.id === orgId) ?? orgs[0] ?? null;

  useEffect(() => {
    if (org && org.id !== orgId) setOrgId(org.id);
  }, [org, orgId]);

  const flags = useQuery({
    queryKey: ["core", "feature_flags", org?.id ?? null],
    enabled: Boolean(org),
    queryFn: async () => {
      const rows = await unwrap<Array<{ module: string; enabled: boolean }>>(
        from("core", "feature_flags").select("module, enabled").eq("org_id", org!.id),
      );
      return new Map(rows.map((r) => [r.module, r.enabled]));
    },
  });

  const switchOrg = useCallback(
    (id: string) => {
      writeOrg(id);
      setOrgId(id);
      // Dados de outra organização não podem aparecer nem por um instante.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "core" || q.queryKey[1] !== "boot" });
    },
    [qc],
  );

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ["core", "boot"] });
    await qc.invalidateQueries({ queryKey: ["core", "feature_flags"] });
  }, [qc]);

  const moduleState = useMemo<ModuleState>(
    () => ({ installed: boot.data?.installed ?? new Set(), flags: flags.data ?? new Map() }),
    [boot.data, flags.data],
  );

  const modules = useMemo<ModuleStatus[]>(() => {
    const byName = new Map(registeredModules.map((m) => [m.name, m]));
    const names = new Set([...moduleState.installed, ...byName.keys()]);
    return [...names].sort().map((name) => {
      const reg = byName.get(name);
      const flag = reg?.featureFlag ?? name;
      return {
        name,
        title: reg?.title ?? name,
        featureFlag: name === "core" ? null : flag,
        installed: moduleState.installed.has(name),
        enabled: reg ? isEnabled(reg, moduleState) : moduleState.installed.has(name) && (moduleState.flags.get(flag) ?? true),
        hasUi: Boolean(reg),
      };
    });
  }, [moduleState]);

  const value = useMemo<SessionInfo>(
    () => ({
      userId,
      email: session!.user.email ?? null,
      org,
      orgs,
      isPlatformAdmin: boot.data?.isPlatformAdmin ?? false,
      aal,
      switchOrg,
      modules,
      refresh,
    }),
    [userId, session, org, orgs, boot.data, aal, switchOrg, modules, refresh],
  );

  if (mfa.isLoading) return null;
  if (mfa.data) {
    return (
      <AuthLayout title="Verificação em duas etapas" subtitle="Digite o código de 6 dígitos do seu aplicativo autenticador.">
        <TotpChallengeForm
          factorId={mfa.data}
          onDone={async () => {
            await refreshAal();
            await qc.invalidateQueries({ queryKey: ["auth", "aal"] });
          }}
        />
        <p style={{ marginTop: 16 }}>
          <Button variant="link" onClick={() => void getClient().auth.signOut()}>
            Sair e entrar com outra conta
          </Button>
        </p>
      </AuthLayout>
    );
  }
  if (boot.isLoading) {
    return (
      <div className="vx-boot" role="status" aria-busy="true">
        <div className="vx-boot__mark">
          <VixioSymbol size={35} />
          <div className="vx-boot__bar" />
          <span>Carregando a sua organização</span>
        </div>
      </div>
    );
  }
  if (boot.error) {
    return (
      <AuthLayout title="Não foi possível carregar">
        <Callout tone="crit">{boot.error instanceof Error ? boot.error.message : "Erro ao falar com o servidor."}</Callout>
        <p style={{ marginTop: 16 }}>
          <Button onClick={() => void boot.refetch()}>Tentar de novo</Button>
        </p>
      </AuthLayout>
    );
  }
  if (!org) {
    return (
      <AuthLayout
        title="Nenhuma organização"
        subtitle={
          boot.data!.isPlatformAdmin || orgs.length === 0
            ? "Você ainda não participa de nenhuma organização."
            : undefined
        }
      >
        <div className="vx-stack">
          <Callout tone="info">
            Peça a quem administra a sua organização um convite para <strong>{session!.user.email}</strong>. Se esta for a
            primeira instalação, crie a organização abaixo: quem cria vira proprietário.
          </Callout>
          <CreateOrganizationForm
            onCreated={async (id) => {
              writeOrg(id);
              setOrgId(id);
              await refresh();
            }}
          />
          <Button variant="link" onClick={() => void getClient().auth.signOut()}>
            Sair
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <SessionProvider value={value}>
      <ModuleStateCtx.Provider value={moduleState}>{children}</ModuleStateCtx.Provider>
    </SessionProvider>
  );
}
